/**
 * CUB MUI Theme
 * ─────────────
 * Drop-in theme for Material UI v5/v6 that skins MUI with the CUB brand.
 *
 *   npm install @mui/material @emotion/react @emotion/styled
 *
 *   // App.tsx
 *   import { ThemeProvider, CssBaseline } from "@mui/material";
 *   import { cubTheme } from "./cubTheme";
 *
 *   <ThemeProvider theme={cubTheme}>
 *     <CssBaseline />
 *     <App />
 *   </ThemeProvider>
 *
 * Fonts: load "Commuters Sans" (or fallback Montserrat) and "Inter" via
 * your @font-face stylesheet or @fontsource packages. Example:
 *   import "@fontsource/inter/400.css";
 *   import "@fontsource/inter/500.css";
 *   import "@fontsource/inter/600.css";
 *   import "@fontsource/inter/700.css";
 *   // + "Commuters Sans" self-hosted (.woff2 files from this repo's /fonts)
 *
 * All values mirror the approved preview cards in /preview. When a token
 * changes there, update the matching block below so the product stays
 * in sync with the design system.
 */

import { createTheme, alpha } from "@mui/material/styles";
import { tokens } from "./tokens";
import { CSSProperties } from "react";

// Adiciona suporte às novas variantes de tipografia do Figma no MUI
declare module "@mui/material/styles" {
  interface TypographyVariants {
    displayL: CSSProperties;
    display: CSSProperties;
    buttonL: CSSProperties;
    buttonS: CSSProperties;
  }
  interface TypographyVariantsOptions {
    displayL?: CSSProperties;
    display?: CSSProperties;
    buttonL?: CSSProperties;
    buttonS?: CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    displayL: true;
    display: true;
    buttonL: true;
    buttonS: true;
  }
}

declare module "@mui/material/BottomNavigation" {
  interface BottomNavigationOwnProps {
    variant?: "brand";
  }
}

declare module "@mui/material/BottomNavigationAction" {
  interface BottomNavigationActionOwnProps {
    variant?: "brand";
  }
}

// Cores semânticas adicionais do CUB (acima das padrão do MUI)
declare module "@mui/material/styles" {
  interface Palette {
    pending: Palette["primary"];
    neutral: Palette["primary"];
  }
  interface PaletteOptions {
    pending?: PaletteOptions["primary"];
    neutral?: PaletteOptions["primary"];
  }
}

// Habilita color="pending" / color="neutral" nos componentes principais
declare module "@mui/material/Chip" {
  interface ChipPropsColorOverrides { pending: true; neutral: true }
  interface ChipClasses { colorPending: string; colorNeutral: string }
  // status: opt-in para o look de badge (bold + uppercase + letter-spacing).
  // Default = label/tag neutro (peso e caixa naturais).
  interface ChipOwnProps { status?: boolean }
}
declare module "@mui/material/Alert" {
  interface AlertPropsColorOverrides { pending: true; neutral: true }
}
// Regra de marca: botões só usam primary (azul) e error (destrutivo).
// Por isso pending/neutral NÃO são adicionados a Button/Fab/IconButton.
declare module "@mui/material/Badge" {
  interface BadgePropsColorOverrides { pending: true; neutral: true }
}
declare module "@mui/material/LinearProgress" {
  interface LinearProgressPropsColorOverrides { pending: true; neutral: true }
}
declare module "@mui/material/CircularProgress" {
  interface CircularProgressPropsColorOverrides { pending: true; neutral: true }
}
declare module "@mui/material/SvgIcon" {
  interface SvgIconPropsColorOverrides { pending: true; neutral: true }
}

// Tamanho padronizado para o Drawer: sm 320px · md 540px · lg 760px
declare module "@mui/material/Drawer" {
  interface DrawerProps {
    size?: "sm" | "md" | "lg";
  }
}

/**
 * Fonte única de tokens: `./tokens.ts`. Não redeclare cores, fontes ou
 * radii aqui — qualquer valor novo entra em tokens.ts e é consumido abaixo.
 */

/* ─────── Theme ─────── */
export const cubTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: tokens.blue,
      dark: tokens.cobalt,
      light: tokens.blueLight,
      contrastText: tokens.white,
    },
    secondary: {
      main: tokens.midnight,
      contrastText: tokens.white,
    },
    success: { main: tokens.success, contrastText: tokens.white },
    warning: { light: tokens.warning200, main: tokens.warning, dark: tokens.warning500, contrastText: tokens.white },
    error: { main: tokens.error, contrastText: tokens.white },
    info: { light: tokens.info200, main: tokens.info, dark: tokens.info500, contrastText: tokens.white },
    pending: { light: tokens.pending200, main: tokens.pending, dark: tokens.pending500, contrastText: tokens.white },
    // color="neutral" — cinza puro (Material Design). Para o tom azulado da
    // marca, use tokens.neutralXXX diretamente em sx; é paleta de apoio.
    neutral: { light: tokens.grey100, main: tokens.grey600, dark: tokens.grey800, contrastText: tokens.white },
    text: {
      primary: tokens.textPrimary,
      secondary: tokens.textSecondary,
      disabled: tokens.textMuted,
    },
    background: {
      default: tokens.bgDefault,
      paper: tokens.bgDefault,
    },
    divider: "rgba(0,0,0,0.12)",
    grey: {
      50: tokens.grey50,
      100: tokens.grey100,
      200: tokens.grey200,
      300: tokens.grey300,
      400: tokens.grey400,
      500: tokens.grey500,
      600: tokens.grey600,
      700: tokens.grey700,
      800: tokens.grey800,
      900: tokens.grey900,
    },
  },

  shape: {
    borderRadius: 8, // padrão MUI; overrides usam tokens semânticos (radiusButton, radiusCard, etc.)
  },

  typography: {
    fontFamily: tokens.fontBody,
    // Display — Commuters Sans (Figma specs)
    displayL: { fontFamily: tokens.fontDisplay, fontWeight: 700, fontSize: tokens.fontSize48, lineHeight: 1.1, letterSpacing: "-0.04em", color: tokens.midnight },
    display: { fontFamily: tokens.fontDisplay, fontWeight: 700, fontSize: tokens.fontSize40, lineHeight: 1.1, letterSpacing: "-0.04em", color: tokens.midnight },
    h1: { fontFamily: tokens.fontDisplay, fontWeight: 700, fontSize: tokens.fontSize32, lineHeight: 1.1, letterSpacing: "-0.04em", color: tokens.midnight },
    h2: { fontFamily: tokens.fontDisplay, fontWeight: 700, fontSize: tokens.fontSize24, lineHeight: 1.1, letterSpacing: "-0.02em", color: tokens.midnight },
    h3: { fontFamily: tokens.fontDisplay, fontWeight: 600, fontSize: tokens.fontSize20, lineHeight: 1.2, letterSpacing: "-0.01em", color: tokens.midnight },

    // MUI standard headers are mapped to Figma scale or kept as fallbacks
    h4: { fontFamily: tokens.fontDisplay, fontWeight: 700, fontSize: 18, lineHeight: 1.2 },
    h5: { fontFamily: tokens.fontDisplay, fontWeight: 700, fontSize: 16, lineHeight: 1.2 },
    h6: { fontFamily: tokens.fontDisplay, fontWeight: 700, fontSize: 14, lineHeight: 1.2 },

    // Body — Inter (Figma specs)
    body1: { fontFamily: tokens.fontBody, fontWeight: 400, fontSize: tokens.fontSize16, lineHeight: 1.5 },
    body2: { fontFamily: tokens.fontBody, fontWeight: 400, fontSize: tokens.fontSize14, lineHeight: 1.5 },
    caption: { fontFamily: tokens.fontBody, fontWeight: 400, fontSize: tokens.fontSize12, lineHeight: 1.5 },

    // Fallbacks and extra CUB styles
    subtitle1: { fontFamily: tokens.fontBody, fontWeight: 500, fontSize: 16, lineHeight: 1.5 },
    subtitle2: { fontFamily: tokens.fontBody, fontWeight: 600, fontSize: 14, lineHeight: 1.4, letterSpacing: "0.05em", textTransform: "uppercase" },
    overline: { fontFamily: tokens.fontBody, fontWeight: 600, fontSize: tokens.fontSize11, letterSpacing: "0.12em", textTransform: "uppercase" },

    // Button typography (Figma specs)
    buttonL: { fontFamily: tokens.fontDisplay, fontWeight: 700, fontSize: tokens.fontSize18, lineHeight: 1.5, letterSpacing: "0.04em", textTransform: "uppercase" },
    button: { fontFamily: tokens.fontDisplay, fontWeight: 700, fontSize: tokens.fontSize16, lineHeight: 1.5, letterSpacing: "0.04em", textTransform: "uppercase" },
    buttonS: { fontFamily: tokens.fontDisplay, fontWeight: 700, fontSize: tokens.fontSize14, lineHeight: 1.5, letterSpacing: "0.04em", textTransform: "uppercase" },
  },

  /* ─────── Component overrides ─────── */
  components: {
    // Convenção CUB: <Icon> usa Material Icons Rounded por padrão.
    // Produtos consumidores devem carregar a fonte:
    //   <link rel="stylesheet" href="https://fonts.googleapis.com/icon?family=Material+Icons+Round" />
    MuiIcon: {
      defaultProps: { baseClassName: "material-icons-round" },
    },

    /* BUTTON — main / outline / text / destructive (=error color)
     * Spec oficial CUB (Button - Specification.pdf):
     *   • Border radius: 4
     *   • Padding (top-bottom / left-right):  L 8/22 · M 6/16 · S 4/10
     *   • Ícones:  L 24×24 · M 20×20 · S 16×16
     *   • Estados (Main / Primary):
     *       Enabled  → bg #417DFA · text #FFF · Elevation/2
     *       Hover    → bg #3466CE · text #FFF · Elevation/4
     *       Focused  → bg #417DFA · text #FFF · Elevation/6
     *       Disabled → bg rgba(0,0,0,0.26) · text tokens.disabledText · sem shadow
     *   • Elevation/2 (oficial):
     *       0 3px 1px -2px rgba(0,0,0,0.20),
     *       0 2px 2px  0   rgba(0,0,0,0.14),
     *       0 1px 5px  0   rgba(0,0,0,0.12)
     *     Elevation/4 e /6 seguem o mesmo padrão MUI (umbra/penumbra/ambient).
     */
    MuiButton: {
      defaultProps: {
        // shadows são parte da spec — elevation precisa estar habilitada
        disableElevation: false,
        disableRipple: false,
      },
      styleOverrides: {
        root: {
          fontFamily: tokens.fontDisplay,
          fontWeight: 700,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          borderRadius: tokens.radiusButton,
          lineHeight: 1.25,
          // text/outlined não recebem elevation; sobrescrito por variante abaixo
          boxShadow: "none",
          "&:active": { transform: "translateY(1px)" },
        },
        // Sizes — paddings oficiais CUB · ícones com tamanho fixo por size
        sizeSmall: {
          fontSize: tokens.fontSize14,
          lineHeight: 1.5,
          padding: "4px 10px",
          "& .MuiButton-startIcon, & .MuiButton-endIcon": {
            "& > *:nth-of-type(1)": { fontSize: 16 },
          },
          "& .MuiButton-startIcon": { marginLeft: -2, marginRight: 6 },
          "& .MuiButton-endIcon": { marginRight: -2, marginLeft: 6 },
        },
        sizeMedium: {
          fontSize: tokens.fontSize16,
          lineHeight: 1.5,
          padding: "6px 16px",
          "& .MuiButton-startIcon, & .MuiButton-endIcon": {
            "& > *:nth-of-type(1)": { fontSize: 20 },
          },
          "& .MuiButton-startIcon": { marginLeft: -4, marginRight: 8 },
          "& .MuiButton-endIcon": { marginRight: -4, marginLeft: 8 },
        },
        sizeLarge: {
          fontSize: tokens.fontSize18,
          lineHeight: 1.5,
          padding: "8px 22px",
          "& .MuiButton-startIcon, & .MuiButton-endIcon": {
            "& > *:nth-of-type(1)": { fontSize: 24 },
          },
          "& .MuiButton-startIcon": { marginLeft: -4, marginRight: 8 },
          "& .MuiButton-endIcon": { marginRight: -4, marginLeft: 8 },
        },

        // Main — filled primary
        containedPrimary: {
          backgroundColor: tokens.blue,
          color: tokens.white,
          // Elevation/2
          boxShadow:
            "0 3px 1px -2px rgba(0,0,0,0.20), 0 2px 2px 0 rgba(0,0,0,0.14), 0 1px 5px 0 rgba(0,0,0,0.12)",
          "&:hover": {
            backgroundColor: tokens.blueHover,
            // Elevation/4
            boxShadow:
              "0 2px 4px -1px rgba(0,0,0,0.20), 0 4px 5px 0 rgba(0,0,0,0.14), 0 1px 10px 0 rgba(0,0,0,0.12)",
          },
          "&:focus-visible": {
            backgroundColor: tokens.blue,
            // Elevation/6
            boxShadow:
              "0 3px 5px -1px rgba(0,0,0,0.20), 0 6px 10px 0 rgba(0,0,0,0.14), 0 1px 18px 0 rgba(0,0,0,0.12)",
          },
          "&.Mui-disabled": {
            backgroundColor: "rgba(0,0,0,0.26)",
            color: tokens.disabledText,
            boxShadow: "none",
          },
        },
        // Destructive — filled error (mesma curva de elevation)
        containedError: {
          backgroundColor: tokens.error,
          color: tokens.white,
          boxShadow:
            "0 3px 1px -2px rgba(0,0,0,0.20), 0 2px 2px 0 rgba(0,0,0,0.14), 0 1px 5px 0 rgba(0,0,0,0.12)",
          "&:hover": {
            backgroundColor: tokens.error500,
            boxShadow:
              "0 2px 4px -1px rgba(0,0,0,0.20), 0 4px 5px 0 rgba(0,0,0,0.14), 0 1px 10px 0 rgba(0,0,0,0.12)",
          },
          "&:focus-visible": {
            backgroundColor: tokens.error,
            boxShadow:
              "0 3px 5px -1px rgba(0,0,0,0.20), 0 6px 10px 0 rgba(0,0,0,0.14), 0 1px 18px 0 rgba(0,0,0,0.12)",
          },
          "&.Mui-disabled": {
            backgroundColor: "rgba(0,0,0,0.26)",
            color: tokens.disabledText,
            boxShadow: "none",
          },
        },
        // Outline — base look (neutral). Hover usa currentColor pra acompanhar
        // a cor semântica (error → vermelho, warning → amber, pending → violeta…).
        // outlinedPrimary abaixo redefine o tint azul para color="primary".
        outlined: {
          border: `1.5px solid ${tokens.grey300}`,
          "&:hover": {
            border: "1.5px solid currentColor",
            backgroundColor: alpha(tokens.grey500, 0.04),
          },
        },
        outlinedPrimary: {
          backgroundColor: tokens.bgDefault,
          color: tokens.blue,
          border: `1.5px solid ${tokens.blue}`,
          "&:hover": {
            backgroundColor: alpha(tokens.blue100, 0.3),
            border: `1.5px solid ${tokens.blue}`,
            color: tokens.blue,
          },
          "&.Mui-disabled": {
            backgroundColor: tokens.bgDefault,
            color: tokens.disabledText,
            border: "1.5px solid rgba(0,0,0,0.26)",
          },
        },
        // Outline — destructive look (borda vermelha em rest)
        outlinedError: {
          backgroundColor: tokens.bgDefault,
          color: tokens.error,
          border: `1.5px solid ${tokens.error}`,
          "&:hover": {
            backgroundColor: alpha(tokens.error100, 0.4),
            border: `1.5px solid ${tokens.error500}`,
            color: tokens.error500,
          },
          "&.Mui-disabled": {
            backgroundColor: tokens.bgDefault,
            color: tokens.disabledText,
            border: "1.5px solid rgba(0,0,0,0.26)",
          },
        },
        // Text — link-style
        textPrimary: {
          color: tokens.blue,
          "&:hover": {
            backgroundColor: tokens.aliceBlue,
            color: tokens.cobalt,
          },
          "&.Mui-disabled": { color: tokens.disabledText },
        },
      },
    },
    /* FAB — Floating Action Button */
    MuiFab: {
      styleOverrides: {
        root: {
          textTransform: "uppercase",
          fontFamily: tokens.fontDisplay,
          fontWeight: 700,
          letterSpacing: "0.04em",
          boxShadow: tokens.shadow2,
          "&:hover": {
            boxShadow: tokens.shadow4,
          },
          "&:active": {
            boxShadow: tokens.shadow8,
          },
        },
        primary: {
          backgroundColor: tokens.blue,
          color: tokens.white,
          "&:hover": {
            backgroundColor: tokens.blueHover,
          },
        },
        secondary: {
          backgroundColor: tokens.midnight,
          color: tokens.white,
          "&:hover": {
            backgroundColor: tokens.grey900,
          },
        },
      },
    },

    /* BUTTON GROUP — herda estilos de MuiButton; override de radius e border */
    MuiButtonGroup: {
      defaultProps: {
        disableElevation: false,
      },
      styleOverrides: {
        root: {
          borderRadius: tokens.radiusButton,
        },
        grouped: {
          fontFamily: tokens.fontDisplay,
          fontWeight: 700,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        },
        groupedContained: {
          "&:not(:last-of-type)": {
            borderColor: "rgba(255,255,255,0.3)",
          },
        },
        groupedOutlined: {
          "&:not(:last-of-type)": {
            borderColor: tokens.blue,
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: "filled",
        size: "medium",
      },
    },
    MuiFormControl: {
      defaultProps: {
        variant: "filled",
      },
    },

    MuiFilledInput: {
      defaultProps: {
        disableUnderline: false,
      },
      styleOverrides: {
        root: {
          backgroundColor: tokens.grey100,
          borderTopLeftRadius: tokens.radiusInput,
          borderTopRightRadius: tokens.radiusInput,
          fontSize: 15,
          color: tokens.textPrimary,
          transition: "background-color .15s",
          "&:hover": { backgroundColor: tokens.grey200 },
          "&.Mui-focused": { backgroundColor: tokens.grey200 },
          "&.Mui-disabled": { backgroundColor: tokens.grey50 },
          // Underline — default / hover / focus
          "&:before": { borderBottom: `1px solid ${tokens.blue}` },
          "&:hover:not(.Mui-disabled):before": { borderBottom: `1px solid ${tokens.blue}` },
          "&:after": { borderBottom: `2px solid ${tokens.blue}` },
          // Error
          "&.Mui-error:before": { borderBottom: `1px solid ${tokens.error}` },
          "&.Mui-error:after": { borderBottom: `2px solid ${tokens.error}` },
          // Disabled underline becomes dotted
          "&.Mui-disabled:before": { borderBottomStyle: "dotted", borderBottomColor: "rgba(0,0,0,0.3)" },
          // Multiline (slot "inputMultiline" não existe em MuiFilledInput em MUI v6 — fica via seletor)
          "&.MuiInputBase-multiline .MuiFilledInput-input": {
            paddingTop: 8,
            paddingBottom: 8,
            paddingLeft: 0,
            paddingRight: 0,
          },
          // Size small (slot "inputSizeSmall" idem — via seletor)
          "&.MuiInputBase-sizeSmall .MuiFilledInput-input": {
            paddingTop: 17,
            paddingBottom: 3,
            fontSize: 14,
          },
          // hiddenLabel — sem label flutuante, reequilibra o padding vertical (mantém
          // a altura total) para centralizar o valor/placeholder. Sem isso, o padding
          // assimétrico do label vazaria e empurraria o valor pro topo.
          "&.MuiInputBase-hiddenLabel .MuiFilledInput-input": {
            paddingTop: 14,
            paddingBottom: 14, // medium: 22+6=28 → 14/14
          },
          "&.MuiInputBase-hiddenLabel.MuiInputBase-sizeSmall .MuiFilledInput-input": {
            paddingTop: 10,
            paddingBottom: 10, // small: 16+4=20 → 10/10
          },
        },
        input: {
          paddingTop: 22,
          paddingBottom: 6,
          paddingLeft: 12,
          paddingRight: 12,
        },
        adornedStart: {
          "& .MuiFilledInput-input": { paddingLeft: 4 },
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily: tokens.fontBody,
          fontSize: 14,
          color: tokens.textSecondary,
          "&.Mui-focused": { color: tokens.blue },
          "&.Mui-error": { color: tokens.error500 },
        },
        filled: {
          transform: "translate(12px, 16px) scale(1)",
          "&.MuiInputLabel-shrink": {
            transform: "translate(12px, 6px) scale(0.78)",
          },
          // small: a posição não-encolhida (16px) é calibrada pro medium e deixava o
          // label ~6px abaixo do centro no campo de 40px (estado vazio). Centraliza em
          // translateY 10px; o estado encolhido (com valor) permanece no topo (6px).
          "&.MuiInputLabel-sizeSmall": {
            transform: "translate(12px, 10px) scale(1)",
            // shrunk: sobe 1px (6→5) p/ dar respiro entre label e valor no small
            "&.MuiInputLabel-shrink": {
              transform: "translate(12px, 5px) scale(0.78)",
            },
          },
        },
      },
    },

    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontFamily: tokens.fontBody,
          fontSize: tokens.fontSize12, // Body XS / Caption
          marginLeft: 12,
          marginRight: 12,
          color: tokens.textMuted,
          "&.Mui-error": { color: tokens.error500 },
        },
      },
    },

    /* SELECT — filled look, same bg as text fields */
    MuiSelect: {
      defaultProps: { variant: "filled" },
      styleOverrides: {
        // Select small: a min-height (1.4375em) do slot resolvia em 15px, deixando
        // o campo ~1.4px mais alto que o input text small. Força fonte 14 p/ alinhar.
        select: {
          // O line-height herdado é fixo (21.56px @15). Como o slot do Select é um
          // <div> (sem height própria como o <input>), isso o deixa ~1.4px mais alto
          // no small. Reancora o line-height em 1.4375em (→20.13px @14).
          "&.MuiInputBase-inputSizeSmall": { fontSize: 14, lineHeight: "1.4375em" },
        },
      },
    },

    // Autocomplete (filled) — espelha o padding do input text base para que a
    // altura e o posicionamento do label fiquem idênticos ao TextField, evitando
    // desalinhamento quando usados lado a lado. Override no slot `root` (escopado
    // sob .MuiAutocomplete-root) para igualar a specificity das regras internas do MUI.
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          "& .MuiAutocomplete-inputRoot.MuiFilledInput-root": { paddingTop: 0, paddingBottom: 0 },
          // small: o MUI adiciona paddingBottom:1 no root (0,3,0) — zera com 0,4,0.
          "& .MuiAutocomplete-inputRoot.MuiInputBase-sizeSmall.MuiFilledInput-root": { paddingBottom: 0 },
          "& .MuiAutocomplete-inputRoot .MuiAutocomplete-input.MuiFilledInput-input": {
            paddingTop: 22,
            paddingBottom: 6,
          },
          "& .MuiAutocomplete-inputRoot.MuiInputBase-sizeSmall .MuiAutocomplete-input.MuiFilledInput-input": {
            paddingTop: 17,
            paddingBottom: 3,
          },
        },
      },
    },

    /* CHIP / BADGE — solid fills, Commuters Sans Bold UPPERCASE */
    MuiChip: {
      styleOverrides: {
        root: ({ ownerState }: any) => ({
          fontFamily: tokens.fontBody,
          fontSize: tokens.fontSize12,
          height: 24,
          borderRadius: tokens.radiusPill,
          paddingLeft: 4,
          paddingRight: 4,
          // status — badge look (bold + uppercase). Default = label/tag neutro.
          ...(ownerState?.status && {
            fontWeight: 700,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
          }),
        }),
        sizeSmall: {
          height: 18,
          fontSize: tokens.fontSize11,
        },
        label: { paddingLeft: 8, paddingRight: 8 },
        labelSmall: { paddingLeft: 6, paddingRight: 6 },
        // deleteIcon — escalado pra altura reduzida do chip CUB (24/18 vs default MUI 32/24).
        // margin-right = 0 pra não duplicar com o root.paddingRight (4) — mantém respiro
        // lateral igual ao top/bottom (~4px medium · ~2.5px small).
        deleteIcon: {
          fontSize: 16,
          width: 16,
          height: 16,
          margin: "0 0 0 -3px",
        },
        deleteIconSmall: {
          fontSize: 13,
          width: 13,
          height: 13,
          margin: "0 -2px 0 -2px",
        },
        // avatar — mesma lógica do deleteIcon: escala pro chip CUB e equilibra
        // o respiro do lado esquerdo ao top/bottom.
        // Default = blue/white (CUB primary). Para customizar por chip, use:
        //   <Chip sx={{ "& .MuiChip-avatar": { bgcolor: ..., color: ... } }} />
        // (sx no Avatar perde para o slot do MUI por specificity).
        avatar: {
          width: 18,
          height: 18,
          fontSize: 10,
          fontWeight: 700,
          backgroundColor: `${tokens.blue} !important`,
          color: `${tokens.white} !important`,
          marginLeft: "-1px",
          marginRight: "-4px",
        },
        avatarSmall: {
          width: 14,
          height: 14,
          fontSize: 9,
          fontWeight: 700,
          backgroundColor: `${tokens.blue} !important`,
          color: `${tokens.white} !important`,
          marginLeft: "-2px",
          marginRight: "-3px",
        },
        colorPrimary: { backgroundColor: tokens.blue, color: tokens.white },
        colorSuccess: { backgroundColor: tokens.success, color: tokens.white },
        colorWarning: { backgroundColor: tokens.warning, color: tokens.white },
        colorError:   { backgroundColor: tokens.error,   color: tokens.white },
        colorInfo:    { backgroundColor: tokens.info,    color: tokens.white },
        colorPending: { backgroundColor: tokens.pending, color: tokens.white },
        outlined: {
          backgroundColor: "transparent",
          borderColor: tokens.grey300,
          color: tokens.textSecondary,
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: ({ ownerState }: any) => {
          const sizes: Record<string, number> = { sm: 320, md: 540, lg: 760 };
          const size = ownerState?.size && sizes[ownerState.size];
          if (!size) return {};
          const horizontal = ownerState.anchor === "top" || ownerState.anchor === "bottom";
          return horizontal
            ? { height: size, maxHeight: "calc(100vh - 32px)" }
            : { width: size, maxWidth: "calc(100vw - 32px)" };
        },
      },
    },
    /* CARD / PAPER — soft shadow + 16px radius (CUB card spec) */
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: tokens.radiusCard,
          padding: "16px 0",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: tokens.fontDisplay,
          fontWeight: 700,
          fontSize: tokens.fontSize24, // H2
          lineHeight: 1.2,
          padding: "16px 24px",
          textAlign: "center",
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: "8px 24px 24px",
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          flexDirection: "column",
          alignItems: "center",
          padding: "0 24px 16px",
          gap: "8px",
          "& > :not(:first-of-type)": {
            marginLeft: 0,
          },
          "& .MuiButton-root": {
            width: "100%",
            maxWidth: 320,
          },
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 1 },
      styleOverrides: {
        root: {
          borderRadius: tokens.radiusCard,
          boxShadow:
            "0 1px 2px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.06)",
          border: "1px solid rgba(0,0,0,0.04)",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: tokens.radiusPaper },
      },
    },

    /* ALERT — CUB info/success/warning/error treatment */
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: tokens.radiusAlert, fontSize: 12 },
        standardInfo:    { backgroundColor: tokens.info100,    color: tokens.info500,    "& .MuiAlert-icon": { color: tokens.info300    } },
        standardSuccess: { backgroundColor: tokens.success100, color: tokens.success500, "& .MuiAlert-icon": { color: tokens.success300 } },
        standardWarning: { backgroundColor: tokens.warning100, color: tokens.warning500, "& .MuiAlert-icon": { color: tokens.warning300 } },
        standardError:   { backgroundColor: tokens.error100,   color: tokens.error500,   "& .MuiAlert-icon": { color: tokens.error300   } },
      },
    },

    /* SWITCH — track in Crayola Blue when checked */
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          "&.Mui-checked": { color: tokens.white },
          "&.Mui-checked + .MuiSwitch-track": {
            backgroundColor: tokens.blue,
            opacity: 1,
          },
        },
        track: { backgroundColor: tokens.grey300, opacity: 1 },
      },
    },

    /* BOTTOM NAVIGATION — variante "brand" (Crayola Blue bg, white icons/labels) */
    MuiBottomNavigation: {
      variants: [
        {
          props: { variant: "brand" },
          style: {
            backgroundColor: tokens.blue,
            "& .MuiBottomNavigationAction-root": {
              color: "rgba(255,255,255,0.55)",
              minWidth: 0,
              "&.Mui-selected": {
                color: tokens.white,
              },
            },
          },
        },
      ],
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            // Garante que o label selecionado também herde o estilo se necessário
          },
        },
      },
    },
    MuiCheckbox: { defaultProps: { color: "primary" } },
    MuiRadio: { defaultProps: { color: "primary" } },

    /* LINK — primary blue, underline on hover */
    MuiLink: {
      styleOverrides: {
        root: {
          color: tokens.blue,
          textDecorationColor: alpha(tokens.blue, 0.4),
          "&:hover": { color: tokens.cobalt, textDecorationColor: tokens.cobalt },
        },
      },
    },

    /* CSS BASELINE — body font + smoothing */
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: tokens.fontBody,
          color: tokens.textPrimary,
          WebkitFontSmoothing: "antialiased",
          MozOsxFontSmoothing: "grayscale",
        },
      },
    },
  },
});

export default cubTheme;

/**
 * Re-export dos tokens para conveniência:
 *   import { cubTheme, tokens } from "@cub-com-br/theme";
 *
 * Quem só precisa dos tokens (gráficos, utilitários) deve usar o subpath
 * para tree-shaking ideal:
 *   import { tokens } from "@cub-com-br/theme/tokens";
 */
export { tokens } from "./tokens";
export type { Tokens } from "./tokens";
